import React from 'react';

/**
 * CRITICAL: Error Boundary Component
 * 
 * This component catches JavaScript errors anywhere in the child component tree,
 * logs those errors, and displays a fallback UI instead of crashing the entire app.
 * 
 * DO NOT REMOVE - This prevents the entire app from crashing when errors occur.
 * 
 * Error boundaries catch errors during:
 * - Rendering
 * - Lifecycle methods
 * - Constructors of the whole tree below them
 * 
 * They do NOT catch errors for:
 * - Event handlers
 * - Asynchronous code (e.g. setTimeout callbacks)
 * - Errors thrown during server-side rendering
 * - Errors thrown in the error boundary itself
 */

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null, 
      errorInfo: null 
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log the error for debugging
    console.error('🚨 Error Boundary caught an error:', error, errorInfo);
    
    // Check if this is a React hydration error (common with authentication)
    const isHydrationError = error.message && (
      error.message.includes('Minified React error #31') ||
      error.message.includes('hydration') ||
      error.message.includes('server-rendered HTML')
    );
    
    // Check if this is an authentication-related error
    const isAuthError = error.message && (
      error.message.includes('Farcaster') ||
      error.message.includes('auth') ||
      error.message.includes('SDK') ||
      error.message.includes('Promise')
    );
    
    if (isHydrationError) {
      console.warn('⚠️ React hydration error detected - this is often caused by authentication state mismatches');
    }
    
    if (isAuthError) {
      console.warn('⚠️ Authentication error detected - clearing auth storage to prevent recurring issues');
      try {
        localStorage.removeItem('farcaster_auth_token');
        localStorage.removeItem('farcaster_user');
        localStorage.removeItem('farcaster_miniapp_user');
        localStorage.removeItem('farcaster_miniapp_token');
      } catch (clearError) {
        console.warn('Failed to clear auth storage:', clearError);
      }
    }
    
    // Update state with error details
    this.setState({
      error,
      errorInfo
    });

    // In production, you would send this to an error reporting service
    if (process.env.NODE_ENV === 'production') {
      // Example: Sentry.captureException(error);
    }
  }

  handleReload = () => {
    // Reset the error boundary and reload the page
    window.location.reload();
  };

  handleReset = () => {
    // Reset the error boundary state
    this.setState({ 
      hasError: false, 
      error: null, 
      errorInfo: null 
    });
  };

  render() {
    if (this.state.hasError) {
      // Determine error type for better user messaging
      const isAuthError = this.state.error?.message && (
        this.state.error.message.includes('Farcaster') ||
        this.state.error.message.includes('auth') ||
        this.state.error.message.includes('SDK') ||
        this.state.error.message.includes('Promise')
      );
      
      const isHydrationError = this.state.error?.message && (
        this.state.error.message.includes('Minified React error #31') ||
        this.state.error.message.includes('hydration')
      );
      
      return (
        <div className="min-h-screen bg-secondary-900 text-white flex items-center justify-center p-4">
          <div className="max-w-md w-full text-center">
            <div className="mb-8">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-100 flex items-center justify-center">
                <div className="text-2xl">
                  {isAuthError ? '🔐' : isHydrationError ? '🔄' : '⚠️'}
                </div>
              </div>
              
              <h1 className="text-2xl font-bold text-white mb-2">
                {isAuthError ? 'Authentication Issue' : 
                 isHydrationError ? 'Loading Issue' : 
                 'Something went wrong'}
              </h1>
              
              <p className="text-secondary-400 mb-6">
                {isAuthError ? 
                  'There was an issue with Farcaster authentication. This usually resolves quickly by reloading the page.' :
                 isHydrationError ?
                  'The page had trouble loading properly. This is usually fixed by refreshing.' :
                  'We\'re sorry, but something unexpected happened. The error has been logged and we\'ll look into it.'}
              </p>
            </div>

            <div className="space-y-3">
              <button
                onClick={this.handleReset}
                className="w-full btn-primary"
              >
                Try Again
              </button>
              
              <button
                onClick={this.handleReload}
                className="w-full btn-secondary"
              >
                Reload Page
              </button>
            </div>

            {/* Show error details in development */}
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details className="mt-8 text-left">
                <summary className="cursor-pointer text-sm font-medium text-secondary-300 mb-2">
                  Error Details (Development Only)
                </summary>
                <div className="bg-secondary-800 p-4 rounded-lg text-xs font-mono text-red-300 overflow-auto max-h-40">
                  <p className="font-bold mb-2">Error:</p>
                  <p className="mb-4">{this.state.error.toString()}</p>
                  
                  {this.state.errorInfo && (
                    <>
                      <p className="font-bold mb-2">Component Stack:</p>
                      <pre className="whitespace-pre-wrap">
                        {this.state.errorInfo.componentStack}
                      </pre>
                    </>
                  )}
                </div>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary; 